import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPen, faBan, faTrash, faArrowUpLong, faArrowDownLong } from '@fortawesome/pro-regular-svg-icons';
import { Rate, RateRuleOperator } from '@spike/boarding-rule-model';
import {
    ExpandableCard,
    ExpandableCardHeader,
    ExpandableCardContent,
    CardTitle,
    CardDescription
} from 'components/UI/V3/ExpandableCard';
import ThreeDotsDropdown from 'components/UI/V3/ThreeDotsDropdown/ThreeDotsDropdown';
import { ThreeDotsDropdownOption } from 'components/UI/V3/ThreeDotsDropdown/ThreeDotsDropdownOption';
import { useBoardingRateRules } from '../../BoardingRateRulesProvider';
import { Service } from 'model/Service';
import styles from './RateRuleCard.module.scss';

interface RateRuleCardProps {
    rateRule: Rate;
}

const formatOperator = (operator: RateRuleOperator): string => {
    switch (operator) {
        case 'gt':
            return 'greater than';
        case 'gte':
            return 'greater or equal than';
        case 'lt':
            return 'less than';
        case 'lte':
            return 'less or equal than';
        default:
            return operator;
    }
};

const formatAmount = (value: number, valueType: string, currency?: string): string => {
    if (valueType === 'percentage') {
        return `${value}%`;
    }
    const symbol = currency === 'USD' ? '$' : currency ?? '$';
    return `${symbol}${value.toFixed(2)}`;
};

const resolveServiceName = (itemId: number, services: Service[]): string => {
    const match = services.find(s => s.id === itemId);
    return match?.name ?? `Service #${itemId}`;
};

const formatDateDescription = (rate: Rate): string => {
    if (rate.startDate.isValid() && rate.endDate.isValid()) {
        return `${rate.startDate.utc().format('ddd, D MMM YYYY')} - ${rate.endDate.utc().format('ddd, D MMM YYYY')}`;
    }
    return 'Always On';
};

export const RateRuleCard: React.FC<RateRuleCardProps> = ({ rateRule }) => {
    const { openEditForm, toggleActive, deleteRateRule, boardingServices } = useBoardingRateRules();

    const rateRulesWithRule = (rateRule.rateRules ?? []).filter(rr => rr.rule);
    const serviceNames = (rateRule.rateItems ?? []).map(item => resolveServiceName(item.itemId, boardingServices));

    const actionOptions: ThreeDotsDropdownOption[] = [
        {
            id: 'edit',
            label: 'Edit',
            icon: faPen,
            onClick: () => openEditForm(rateRule)
        },
        {
            id: 'toggle',
            label: rateRule.active ? 'Deactivate' : 'Activate',
            icon: faBan,
            onClick: () => toggleActive(rateRule.id)
        },
        {
            id: 'delete',
            label: 'Delete',
            icon: faTrash,
            onClick: () => deleteRateRule(rateRule.id)
        }
    ];

    const handleDropdownClick = (e: React.MouseEvent) => {
        e.stopPropagation();
    };

    return (
        <ExpandableCard defaultExpanded={true}>
            <ExpandableCardHeader>
                <div className={styles.titleSection}>
                    <CardTitle>{rateRule.name}</CardTitle>
                    <CardDescription>{formatDateDescription(rateRule)}</CardDescription>
                </div>
                <div
                    className={styles.actionsDropdown}
                    onClick={handleDropdownClick}
                >
                    <ThreeDotsDropdown
                        options={actionOptions}
                        direction="down"
                    />
                </div>
            </ExpandableCardHeader>

            <ExpandableCardContent>
                <div className={styles.contentGrid}>
                    <div className={styles.column}>
                        <h4 className={styles.columnTitle}>Boarding Services</h4>
                        <ul className={styles.servicesList}>
                            {serviceNames.map((name, index) => (
                                <li
                                    key={index}
                                    className={styles.serviceItem}
                                >
                                    {name}
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div className={styles.column}>
                        <h4 className={styles.columnTitle}>Rules</h4>
                        <ul className={styles.rulesList}>
                            {rateRulesWithRule.map((rr, index) => {
                                const r = rr.rule!;
                                return (
                                    <li
                                        key={index}
                                        className={styles.ruleItem}
                                    >
                                        {'When occupancy is '}
                                        <strong>
                                            {`${formatOperator(r.condition.operator)} ${r.condition.threshold}%`}
                                        </strong>{' '}
                                        <strong>{r.action.type}</strong>
                                        {' rate by '}
                                        <strong>
                                            {formatAmount(r.action.value, r.action.valueType, r.action.currency)}
                                        </strong>{' '}
                                        <FontAwesomeIcon
                                            icon={r.action.type === 'increase' ? faArrowUpLong : faArrowDownLong}
                                            className={
                                                r.action.type === 'increase' ? styles.increaseIcon : styles.decreaseIcon
                                            }
                                        />
                                    </li>
                                );
                            })}
                        </ul>
                    </div>
                </div>
            </ExpandableCardContent>
        </ExpandableCard>
    );
};
